import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as MediaLibrary from 'expo-media-library';
import * as Sharing from 'expo-sharing';
import * as Haptics from 'expo-haptics';

/**
 * Detect if the current browser is Safari on iOS (including PWA)
 * @returns {boolean}
 */
export function isIOSSafari() {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad with iPadOS
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua); // Exclude iOS Chrome, Firefox, Edge, Opera
  
  return isIOS && isSafari;
}

/**
 * Check if Web Share API with file support is available
 * @returns {boolean}
 */
export function canUseWebShareFiles() {
  if (typeof navigator === 'undefined' || !navigator.canShare) return false;
  
  // Test if file sharing is supported by trying to share a dummy file
  try {
    const testFile = new File(['test'], 'test.png', { type: 'image/png' });
    return navigator.canShare({ files: [testFile] });
  } catch {
    return false;
  }
}

/**
 * Build a download filename with a real extension.
 * Cross-origin CDN URLs ignore the HTML `download` attribute unless we fetch
 * as a blob first, so the filename we attach to the object URL matters.
 */
function resolveDownloadFilename(url, filename, mimeType) {
  const extension =
    getExtensionFromFilename(filename) ||
    getExtensionFromUrl(url) ||
    getExtensionFromMimeType(mimeType) ||
    'bin';
  const baseName = filename
    ? String(filename).replace(/\.[a-zA-Z0-9]+$/, '')
    : `media-${Date.now()}`;
  // Keep filenames filesystem-safe without mangling the user's title.
  const safeBase = baseName.replace(/[/\\?%*:|"<>]/g, '-').trim() || `media-${Date.now()}`;
  return `${safeBase}.${extension}`;
}

/**
 * Trigger a same-origin blob download via a temporary object URL.
 * Required for cross-origin CDN media: browsers ignore `download` on remote
 * URLs and navigate/play the file instead (especially videos).
 */
function triggerBlobDownload(blob, fullFilename) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fullFilename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Defer revoke so the browser can start reading the blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
  }
}

/**
 * Last-resort download when blob fetch is blocked (CORS / SW / network).
 * Cross-origin `download` is often ignored, so open in a new tab as a
 * usable Save-As path instead of hard-failing with "Failed to fetch".
 */
function triggerDirectUrlDownload(url, fullFilename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fullFilename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function fetchMediaForDownload(url) {
  // no-store avoids a browser/CDN cache entry served without ACAO (Vary: Origin
  // mismatches). omit credentials — CDN objects are public and cookies can
  // trigger stricter CORS checks.
  const response = await fetch(url, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error('Failed to fetch media');
  }
  return response;
}

/**
 * Download sequential MP3 parts as one file. TTS chunk audio is generated in
 * the same format and the backend's combined artifact uses the same byte-wise
 * concatenation, so this also supports legacy cache entries without a stored
 * combined-file URL.
 */
export async function downloadCombinedMedia(urls, filename) {
  const validUrls = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (Platform.OS !== 'web' || validUrls.length === 0) {
    return { success: false, message: 'Combined audio download is unavailable' };
  }

  try {
    const responses = await Promise.all(validUrls.map(fetchMediaForDownload));
    const parts = await Promise.all(responses.map(response => response.arrayBuffer()));
    const blob = new Blob(parts, { type: 'audio/mpeg' });
    const fullFilename = resolveDownloadFilename(validUrls[0], filename, blob.type);
    triggerBlobDownload(blob, fullFilename);
    return { success: true, message: 'Download complete' };
  } catch (error) {
    console.error('Combined media download error:', error);
    return { success: false, message: error.message || 'Failed to download file' };
  }
}

/**
 * Fetch remote media and force a file download in desktop browsers.
 * @param {string} url - The URL of the media to download
 * @param {string} filename - The desired filename (extension optional)
 * @param {Object} options - Optional callbacks
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function downloadViaBlob(url, filename, options = {}) {
  const { onProgress, onSuccess, onError } = options;

  try {
    onProgress?.(0.1);

    let response;
    try {
      response = await fetchMediaForDownload(url);
    } catch (firstError) {
      // One retry covers transient SW/network blips before falling back.
      response = await fetchMediaForDownload(url).catch(() => {
        throw firstError;
      });
    }

    onProgress?.(0.4);

    // Prefer streaming progress when Content-Length is available so large
    // video downloads don't feel stuck while the blob buffers.
    const total = Number(response.headers.get('content-length')) || 0;
    let blob;
    if (total > 0 && response.body && typeof ReadableStream !== 'undefined') {
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.byteLength;
        onProgress?.(Math.min(0.9, 0.4 + (received / total) * 0.5));
      }
      blob = new Blob(chunks, { type: response.headers.get('content-type') || undefined });
    } else {
      blob = await response.blob();
      onProgress?.(0.85);
    }

    const fullFilename = resolveDownloadFilename(
      url,
      filename,
      blob.type || response.headers.get('content-type'),
    );

    triggerBlobDownload(blob, fullFilename);
    onProgress?.(1);
    onSuccess?.('Download complete');
    return { success: true, message: 'Download complete' };
  } catch (error) {
    console.error('Blob download error:', error);

    // Blob fetch failed (CORS / SW / network). Open the CDN URL directly so
    // the user can still save the file instead of seeing "Failed to fetch".
    try {
      const fullFilename = resolveDownloadFilename(url, filename, null);
      triggerDirectUrlDownload(url, fullFilename);
      onProgress?.(1);
      const message = 'Opened in a new tab — use Save if the download did not start';
      onSuccess?.(message);
      return { success: true, message, usedFallback: true };
    } catch (fallbackError) {
      console.error('Direct download fallback error:', fallbackError);
      const message = error.message || 'Failed to download file';
      onError?.(message);
      return { success: false, message };
    }
  }
}

/**
 * Download media via Web Share API (for iOS Safari)
 * @param {string} url - The URL of the media to download
 * @param {string} filename - The desired filename
 * @param {Object} options - Optional callbacks
 * @returns {Promise<{success: boolean, message: string, needsFallback?: boolean}>}
 */
export async function downloadViaWebShare(url, filename, options = {}) {
  const { onProgress, onSuccess, onError } = options;

  let blob = null;
  let fullFilename = null;

  try {
    onProgress?.(0.1);

    // Fetch the media as a blob (same CORS-safe options as downloadViaBlob)
    const response = await fetchMediaForDownload(url);

    onProgress?.(0.5);

    blob = await response.blob();
    fullFilename = resolveDownloadFilename(
      url,
      filename,
      blob.type || response.headers.get('content-type'),
    );
    const mimeType = blob.type || getMimeType(fullFilename) || 'application/octet-stream';

    onProgress?.(0.8);

    // Create a File object from the blob
    const file = new File([blob], fullFilename, { type: mimeType });

    // Prefer the share sheet when the browser can share this file type
    if (navigator.canShare?.({ files: [file] })) {
      onProgress?.(1);
      await navigator.share({
        files: [file],
        title: fullFilename,
      });
      onSuccess?.('Share sheet opened - tap Save to keep the file');
      return { success: true, message: 'Share sheet opened' };
    }

    // Share unsupported for this file — fall through to blob download below
  } catch (error) {
    // User cancelled the share sheet - not an error
    if (error?.name === 'AbortError') {
      return { success: true, message: 'Share cancelled' };
    }
    console.error('Web Share error:', error);
    // If fetch already failed, don't pretend a blob download will work
    if (!blob) {
      const message = error.message || 'Share failed';
      onError?.(message);
      return { success: false, message, needsFallback: true };
    }
  }

  // Fall back to a forced blob download with the bytes we already fetched
  try {
    triggerBlobDownload(blob, fullFilename || resolveDownloadFilename(url, filename, blob.type));
    onProgress?.(1);
    onSuccess?.('Download complete');
    return { success: true, message: 'Download complete' };
  } catch (error) {
    const message = error.message || 'Share failed';
    onError?.(message);
    return { success: false, message, needsFallback: true };
  }
}

/**
 * Download media and save to device Photos library (iOS/Android) or trigger browser download (web)
 * @param {string} url - The URL of the media to download
 * @param {string} filename - The desired filename
 * @param {Object} options - Optional configuration
 * @param {Function} options.onProgress - Progress callback (progress: 0-1)
 * @param {Function} options.onSuccess - Success callback with message
 * @param {Function} options.onError - Error callback with error message
 * @param {boolean} options.shareInstead - If true, opens share sheet instead of saving to Photos
 * @returns {Promise<{success: boolean, message: string, needsFallback?: boolean}>}
 */
export async function downloadMedia(url, filename, options = {}) {
  const { onProgress, onSuccess, onError, shareInstead = false } = options;

  // Web: Handle download
  if (Platform.OS === 'web') {
    // iOS Safari: Prefer Web Share (Save Video / Save Image), then blob download
    if (isIOSSafari()) {
      if (canUseWebShareFiles()) {
        return await downloadViaWebShare(url, filename, options);
      }
      // Some iOS browsers can still blob-download; try before asking for manual save
      const blobResult = await downloadViaBlob(url, filename, options);
      if (blobResult.success) return blobResult;
      return { success: false, message: 'iOS Safari requires manual save', needsFallback: true };
    }

    // Desktop / Android browsers: fetch as blob so cross-origin CDN URLs
    // actually download instead of navigating to (and playing) the file.
    return await downloadViaBlob(url, filename, options);
  }

  // Native (iOS/Android): Download to cache, then save to Photos or share
  try {
    // Generate a unique filename with extension
    const extension = getExtensionFromUrl(url) || getExtensionFromFilename(filename) || 'mp4';
    // Strip existing extension from filename to avoid doubling (e.g. "audio.mp3" + ".mp3" = "audio.mp3.mp3")
    const baseName = filename ? filename.replace(/\.[a-zA-Z0-9]+$/, '') : `media-${Date.now()}`;
    const localFilename = `${baseName}.${extension}`;
    const localUri = `${FileSystem.cacheDirectory}${localFilename}`;

    // The device Photos library only accepts images and videos. Everything
    // else (PDFs, documents, audio, archives, code, etc.) must go through the
    // share sheet, which lets the user "Save to Files" on iOS or pick a
    // destination on Android. Routing a non-media file to MediaLibrary throws
    // "This URL does not contain a valid asset type" on iOS and fails silently
    // on Android.
    const ext = extension.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'tiff', 'tif', 'bmp', 'avif'];
    const videoExtensions = ['mp4', 'mov', 'm4v', 'avi', 'webm', 'mkv', '3gp', 'qt', 'mpg', 'mpeg'];
    const isPhotosCompatible = imageExtensions.includes(ext) || videoExtensions.includes(ext);
    const shouldShare = shareInstead || !isPhotosCompatible;

    // Download the file with progress tracking
    const downloadResumable = FileSystem.createDownloadResumable(
      url,
      localUri,
      // Keep desktop downloads in the app process. The background transfer
      // daemon can fail to create its file when switching between locally
      // signed Debug and sandboxed distribution builds of the same app.
      Platform.OS === 'macos' ? { sessionType: FileSystem.FileSystemSessionType.FOREGROUND } : {},
      (downloadProgress) => {
        const progress = downloadProgress.totalBytesWritten / downloadProgress.totalBytesExpectedToWrite;
        onProgress?.(progress);
      }
    );

    const downloadResult = await downloadResumable.downloadAsync();
    
    if (!downloadResult?.uri || !(downloadResult.status >= 200 && downloadResult.status < 300)) {
      throw new Error('Download failed. Please try again.');
    }

    // Non-media files (and explicit share requests) go through the share
    // sheet. macOS has no Photos library -- always use share sheet there too.
    if (shouldShare || Platform.OS === 'macos') {
      return await shareMedia(downloadResult.uri, { onSuccess, onError });
    }

    // Otherwise, save to Photos (iOS/Android only)
    return await saveToPhotos(downloadResult.uri, { onSuccess, onError });

  } catch (error) {
    console.error('Media download error:', error);
    const message = error.message || 'Failed to download media';
    onError?.(message);
    return { success: false, message };
  }
}

/**
 * Save a local file to the device's Photos library
 * @param {string} localUri - Local file URI to save
 * @param {Object} options - Optional callbacks
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function saveToPhotos(localUri, options = {}) {
  const { onSuccess, onError } = options;

  try {
    if (Platform.OS === 'ios') {
      // Add-only access is enough to write a new asset; we do not need
      // broad photo-library read permission.
      const { status } = await MediaLibrary.requestPermissionsAsync(true);
      if (status !== 'granted') {
        const message = 'Photo library access required to save videos';
        onError?.(message);
        return { success: false, message, permissionDenied: true };
      }
    }

    // Android 10+ can insert into MediaStore without READ_MEDIA_*. If that
    // fails (older devices / OEM quirks), fall through to the share sheet.
    let asset = null;
    try {
      asset = await MediaLibrary.createAssetAsync(localUri);
    } catch (createError) {
      if (Platform.OS === 'android') {
        return shareMedia(localUri, options);
      }
      throw createError;
    }
    
    if (!asset) {
      throw new Error('Failed to create asset in Photos');
    }

    // Haptic feedback on success
    await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);

    // Clean up temp file
    try {
      await FileSystem.deleteAsync(localUri, { idempotent: true });
    } catch (cleanupError) {
      // Ignore cleanup errors
    }

    const message = 'Saved to Photos';
    onSuccess?.(message);
    return { success: true, message };

  } catch (error) {
    console.error('Save to Photos error:', error);
    const message = error.message || 'Failed to save to Photos';
    onError?.(message);
    return { success: false, message };
  }
}

/**
 * Open the native share sheet for a local file
 * @param {string} localUri - Local file URI to share
 * @param {Object} options - Optional callbacks
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function shareMedia(localUri, options = {}) {
  const { onSuccess, onError } = options;

  try {
    // Check if sharing is available
    const isAvailable = await Sharing.isAvailableAsync();
    
    if (!isAvailable) {
      const message = 'Sharing is not available on this device';
      onError?.(message);
      return { success: false, message };
    }

    // Open share sheet
    const shareResult = await Sharing.shareAsync(localUri, {
      mimeType: getMimeType(localUri),
      dialogTitle: 'Share Media',
    });

    if (shareResult?.cancelled || shareResult?.canceled) {
      return { success: false, cancelled: true, message: 'Save cancelled' };
    }

    // Haptic feedback
    await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    // Clean up temp file after a delay (user may still be sharing)
    setTimeout(async () => {
      try {
        await FileSystem.deleteAsync(localUri, { idempotent: true });
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
    }, 60000); // Clean up after 1 minute

    const message = Platform.OS === 'macos' ? 'File saved' : 'Share sheet opened';
    onSuccess?.(message);
    return { success: true, message };

  } catch (error) {
    console.error('Share media error:', error);
    const message = error.message || 'Failed to share media';
    onError?.(message);
    return { success: false, message };
  }
}

/**
 * Download media and immediately open share sheet
 * @param {string} url - The URL of the media to download
 * @param {string} filename - The desired filename
 * @param {Object} options - Optional callbacks
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function downloadAndShare(url, filename, options = {}) {
  return downloadMedia(url, filename, { ...options, shareInstead: true });
}

// Helper functions

function getExtensionFromUrl(url) {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const match = pathname.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1].toLowerCase() : null;
  } catch {
    // Try simple extraction
    const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    return match ? match[1].toLowerCase() : null;
  }
}

function getExtensionFromFilename(filename) {
  if (!filename) return null;
  const match = String(filename).match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : null;
}

function getExtensionFromMimeType(mimeType) {
  if (!mimeType || typeof mimeType !== 'string') return null;
  const mime = mimeType.split(';')[0].trim().toLowerCase();
  const map = {
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
    'video/x-matroska': 'mkv',
    'video/x-msvideo': 'avi',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'image/svg+xml': 'svg',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/aac': 'aac',
    'audio/mp4': 'm4a',
    'audio/ogg': 'ogg',
    'audio/flac': 'flac',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
  };
  return map[mime] || null;
}

function getMimeType(uri) {
  const extension = getExtensionFromUrl(uri) || getExtensionFromFilename(uri);
  const mimeTypes = {
    // Video
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    // Image
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    svg: 'image/svg+xml',
    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    aac: 'audio/aac',
    m4a: 'audio/mp4',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    csv: 'text/csv',
    txt: 'text/plain',
    md: 'text/markdown',
    markdown: 'text/markdown',
    json: 'application/json',
    xml: 'application/xml',
    html: 'text/html',
    rtf: 'application/rtf',
    // Archives
    zip: 'application/zip',
    rar: 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
  };
  return mimeTypes[extension] || 'application/octet-stream';
}

export default {
  downloadMedia,
  saveToPhotos,
  shareMedia,
  downloadAndShare,
  downloadViaBlob,
  isIOSSafari,
  canUseWebShareFiles,
  downloadViaWebShare,
};
